import type { Cell, Grid as ContourGridType } from '../features/settings/utils/contourTracer';
import type { GridSettings } from '../types/types';
import * as Honeycomb from 'honeycomb-grid';
import type { GridService, Hex } from '../services/grid.service';
import { squareGrid, createPointyHexGrid, offsetToAxial } from '../features/settings/utils/gridUtils';

/**
 * Defines the contract for all grid-related calculations.
 * This allows us to swap between 'hex' and 'square' logic seamlessly.
 */
export interface GridStrategy {
  readonly type: 'hex' | 'square';
  getCellFromCoordinates(point: { x: number; y: number }): Cell | null;
  getCellRenderData(cell: Cell): { x: number; y: number; width: number; height: number; points?: string } | null;
  getContourGrid(): ContourGridType<[number, number]> | null;
  getGridLines(bounds: { x1: number; y1: number; x2: number; y2: number }): { key: string; points: number[] }[];
  getGridLabels(bounds: { x1: number; y1: number; x2: number; y2: number }): { key: string; x: number; y: number; text: string }[];
}

/**
 * Implements the GridStrategy for a square grid.
 */
export class SquareGridStrategy implements GridStrategy {
  readonly type = 'square';

  constructor(private gridService: GridService, private settings: GridSettings, private origin: { x: number; y: number }) {}

  getCellFromCoordinates(point: { x: number; y: number }): Cell | null {
    const { size } = this.settings;
    const { x: originX, y: originY } = this.origin;
    const col = Math.floor((point.x - originX) / size);
    const row = Math.floor((point.y - originY) / size);
    return { row, col };
  }

  getCellRenderData(cell: Cell): { x: number; y: number; width: number; height: number; } | null {
    const { size } = this.settings;
    const { x: originX, y: originY } = this.origin;
    const x = cell.col * size + originX;
    const y = cell.row * size + originY;
    return { x, y, width: size, height: size };
  }

  getContourGrid(): ContourGridType<[number, number]> | null {
    return squareGrid;
  }

  getGridLines(bounds: { x1: number; y1: number; x2: number; y2: number }): { key: string; points: number[] }[] {
    const lines: { key: string; points: number[] }[] = [];
    const { size } = this.settings;
    const { x: startX, y: startY } = this.origin;

    for (let x = startX % size; x < bounds.x2; x += size) {
      if (x >= bounds.x1) lines.push({ key: `v-${x}`, points: [x, bounds.y1, x, bounds.y2] });
    }
    for (let y = startY % size; y < bounds.y2; y += size) {
      if (y >= bounds.y1) lines.push({ key: `h-${y}`, points: [bounds.x1, y, bounds.x2, y] });
    }
    return lines;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getGridLabels(bounds: { x1: number; y1: number; x2: number; y2: number }): { key: string; x: number; y: number; text: string }[] {
    // Implementation for square grid labels can be added here if needed.
    return [];
  }
}

/**
 * Implements the GridStrategy for a hexagonal grid.
 */
export class HexGridStrategy implements GridStrategy {
  readonly type = 'hex';

  constructor(private gridService: GridService) {}

  getCellFromCoordinates(point: { x: number; y: number }): Cell | null {
    const hex = this.gridService.pointToHex(point);
    return hex ? { row: hex.row, col: hex.col } : null;
  }

  getCellRenderData(cell: Cell): { x: number; y: number; width: number; height: number; points?: string } | null {
    const axialCoords = offsetToAxial(cell);
    // Create a single hex on-demand to get its render data.
    const factory = this.gridService.getFactory();
    const grid = new Honeycomb.Grid(factory);
    const hex = grid.get({ x: axialCoords.q, y: axialCoords.r });
    if (!hex) return null;

    const points = (hex.corners ?? []).map((p: { x: number, y: number }) => `${p.x},${p.y}`).join(' ');
    return { x: hex.x, y: hex.y, width: hex.width, height: hex.height, points };
  }

  getContourGrid(): ContourGridType<[number, number]> | null {
    // This util will need to be adapted to create its own grid if it relies on a populated one.
    // For now, assuming it can work with an empty grid or we create one.
    const factory = this.gridService.getFactory();
    return createPointyHexGrid(new Honeycomb.Grid(factory));
  }

  getGridLines(bounds: { x1: number; y1: number; x2: number; y2: number }): { key: string; points: number[] }[] {
    const hexesInBounds = this.gridService.rectangleFromPoint({
      width: bounds.x2 - bounds.x1,
      height: bounds.y2 - bounds.y1,
      start: { x: bounds.x1, y: bounds.y1 }
    });

    return (hexesInBounds ?? []).map((hex: Hex) => {
      const points = (hex.corners ?? []).flatMap((p: { x: number, y: number }) => [p.x, p.y]);
      return { key: `hex-${hex.col}-${hex.row}`, points: [...points, points[0]!, points[1]!] };
    });
  }

  getGridLabels(bounds: { x1: number; y1: number; x2: number; y2: number }): { key: string; x: number; y: number; text: string }[] {
    const hexesInBounds = this.gridService.rectangleFromPoint({ width: bounds.x2 - bounds.x1, height: bounds.y2 - bounds.y1, start: { x: bounds.x1, y: bounds.y1 } });
    return (hexesInBounds ?? []).map((hex: Hex) => {
      const { x, y } = hex.center;
      const colLabel = String.fromCharCode(65 + (hex.col % 26));
      return { key: `label-hex-${hex.col}-${hex.row}`, x, y, text: `${colLabel}${hex.row + 1}` };
    });
  }
}